import type {
  CompanyType,
  LicenseDocumentResponse,
  LicenseResponse,
} from "@services/licensing/types";
import { DocumentTypes } from "@services/licensing/types";
import type { UserResponse } from "@services/user/types";
import { VENDOR_COMPANY_TYPE_CODE } from "./constants";
import type { VendorLicenseRequestFormData } from "./schema/vendorLicenseRequestFormSchema";

const appendExtensionFromUrl = (baseName: string, url: string) => {
  const cleanUrl = url.split("?")[0].split("#")[0];
  const lastDotIndex = cleanUrl.lastIndexOf(".");
  if (lastDotIndex === -1) return baseName;

  const extension = cleanUrl.substring(lastDotIndex + 1);
  return extension ? `${baseName}.${extension}` : baseName;
};

const mapUserRoles = (roles: string[]): string => {
  return (
    roles
      .filter((role) => ["vendor-master", "licensed-company-agent"].includes(role))
      .map((role) => (role === "vendor-master" ? "Company Agent" : "Licensed Agent"))[0] ?? ""
  );
};

const getApplicantDetails = (license: LicenseResponse, user: UserResponse | null) => {
  if (!user) {
    return {
      applicantEmail: license.applicantEmail ?? "",
      applicantName: license.applicantName ?? "",
      applicantPosition: license.applicantPosition ?? "",
    };
  }

  return {
    applicantEmail: user.email ?? "",
    applicantName: user.name ?? "",
    applicantPosition: mapUserRoles(user.roles ?? []),
  };
};

const mapLicenseDocumentToUploadEntry = (
  document: LicenseDocumentResponse | undefined,
  fallbackName: string,
) => {
  if (!document?.documentUrl) return [];

  return [
    {
      name: document.originalFileName ?? appendExtensionFromUrl(fallbackName, document.documentUrl),
      url: document.documentUrl,
      type: document.documentType,
    },
  ];
};

export const mapVendorLicenseToRenewalForm = (
  license: LicenseResponse,
  user: UserResponse | null,
): VendorLicenseRequestFormData => {
  const documents = license.documents ?? [];
  const incorporationDocument = documents.find(
    (document) => document.documentType === DocumentTypes.COMPANY_INCORPORATION,
  );
  const applicantDetails = getApplicantDetails(license, user);

  return {
    companyName: license.companyName ?? "",
    companyNameArabic: license.companyNameArabic ?? "",
    companyTypes: Array.isArray(license.companyTypes)
      ? license.companyTypes.map(({ companyTypeCode }) => companyTypeCode)
      : typeof license.companyTypes === "string"
        ? license.companyTypes
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    companyAddress: license.companyAddress ?? "",
    telephoneNumber: license.telephoneNumber ?? "",
    mobileNumber: license.mobileNumber ?? "",
    faxNumber: String(license.faxNumber ?? ""),
    companyEmail: license.companyEmail ?? "",
    applicantEmail: applicantDetails.applicantEmail,
    applicantName: applicantDetails.applicantName,
    applicantPosition: applicantDetails.applicantPosition,
    natureOfBusinessInPof: license.natureOfBusinessInPof ?? "",
    numberOfEmployees:
      license.numberOfEmployees !== undefined && license.numberOfEmployees !== null
        ? String(license.numberOfEmployees)
        : "",
    numberOfVehicles:
      license.numberOfVehicles !== undefined && license.numberOfVehicles !== null
        ? String(license.numberOfVehicles)
        : "",
    tradingLicenseNumber:
      license.tradingLicenseNumber !== undefined && license.tradingLicenseNumber !== null
        ? String(license.tradingLicenseNumber)
        : "",
    tradingLicenseValidityDate: "",
    tradeLicenseIssuingAuthority: license.tradeLicenseIssuingAuthority ?? "",
    purposeOfRequest: license.purposeOfRequest ?? "",
    tradeLicenseDocumentUrl: [],
    companyIncorporationDocumentUrl: mapLicenseDocumentToUploadEntry(
      incorporationDocument,
      "company-incorporation",
    ),
  };
};

export const getCompanyTypeOptions = (companyTypes: CompanyType[], isVendorDisabled: boolean) =>
  companyTypes.map((option) => ({
    value: option.companyTypeCode,
    label: option.companyTypeDesc,
    // make the is_vendor option disabled if it was automatically added
    ...(option.companyTypeCode === VENDOR_COMPANY_TYPE_CODE && isVendorDisabled
      ? { disabled: true }
      : {}),
  }));
